import { useRef } from 'react'
import { motion } from 'framer-motion'
import PageLayout from '../components/PageLayout'
import PageHero from '../components/PageHero'
import { useInView } from '../hooks/useInView'

type Tag = 'Product' | 'Cortex' | 'Agents' | 'Platform' | 'Policy'

const updates: {
  date: string
  tag: Tag
  title: string
  summary: string
  details: string[]
}[] = [
  {
    date: 'October 14, 2026',
    tag: 'Product',
    title: 'Affiliate tools',
    summary:
      'Your members, creators, and partner communities can now get their own affiliate links. Anyone who brings people in or sells something gets credit, and payouts happen automatically through Stripe Connect.',
    details: [
      'Affiliate links for every product and group in your marketplace.',
      'An affiliate dashboard where each person sees their own clicks, sales, and earnings.',
      'Commission payouts sent automatically through Stripe Connect, no spreadsheets required.',
      'Works for members, creators, influencers, and partner communities alike.',
    ],
  },
  {
    date: 'April 18, 2026',
    tag: 'Agents',
    title: 'Agent-initiated checkout, generally available',
    summary:
      'AI assistants can now complete purchases inside an Oak marketplace on behalf of a member. You decide what agents can see, do, and buy.',
    details: [
      'Per-marketplace allowlists for agent vendors, plus per-listing opt-in.',
      'Granular spend limits, category filters, and auto-hold for high-value transactions.',
      'Full history: every agent step, prompt, and order shown in your Cortex dashboard.',
    ],
  },
  {
    date: 'April 9, 2026',
    tag: 'Cortex',
    title: 'Demand forecasts in every Cortex tier',
    summary:
      'Every Cortex tier can now see what your community is likely to want over the next ninety days, with a one-click export to CSV or webhook.',
    details: [
      'See what is trending by category or by individual listing.',
      'How sure we are is shown inline and included in API responses.',
      'Basic tier limited to 30 days; Pro and Unlimited cover 90+.',
    ],
  },
  {
    date: 'March 27, 2026',
    tag: 'Product',
    title: 'Threaded comments on listings',
    summary:
      'Threaded discussion now lives on every listing page. You can pin updates, members can ask questions, and sellers can answer without leaving the listing.',
    details: [
      'Pinned notes from the people who run the community at the top of each thread.',
      'Mention, quote, and react — all live across web and mobile.',
      'Notifications respect each member\'s Oak-wide settings.',
    ],
  },
  {
    date: 'March 14, 2026',
    tag: 'Platform',
    title: 'Stripe Connect embedded onboarding',
    summary:
      'Sellers now set up payouts inside your marketplace, with no redirect. First-time sellers get paid sooner and with far less back-and-forth.',
    details: [
      'Embedded Stripe components rendered with your brand tokens.',
      'Fewer steps and fewer drop-offs for first-time sellers.',
      'Verification status and next steps shown right in the seller dashboard.',
    ],
  },
  {
    date: 'February 29, 2026',
    tag: 'Product',
    title: 'White-label email domains',
    summary:
      'Order and account email now sends from your domain, not ours. Set up DKIM and SPF from your dashboard; Oak keeps an eye on deliverability.',
    details: [
      'Custom domain wizard with pre-flight DNS checks.',
      'Deliverability monitoring with per-marketplace health scores.',
      'Per-template branding controls for headers, footers, and accent colors.',
    ],
  },
  {
    date: 'February 11, 2026',
    tag: 'Cortex',
    title: 'Anomaly alerts',
    summary:
      'Cortex now lets you know when something meaningful changes around your products — a sudden spike in refunds, a category going quiet, a price that looks off.',
    details: [
      'Per-metric thresholds or automatic baselines.',
      'Delivery to email, Slack, or webhook.',
      'Replayable history lets you revisit any alert from the last 90 days.',
    ],
  },
  {
    date: 'January 22, 2026',
    tag: 'Platform',
    title: 'SDK v2 public beta',
    summary:
      'The new SDK replaces the 2024 beta with typed clients for listings, orders, messaging, and Cortex queries. Migration guide and codemods included.',
    details: [
      'TypeScript-first clients with generated types.',
      'Codemod to migrate from the 2024 SDK in most codebases.',
      'OpenAPI and GraphQL schemas published at docs.oakplatforms.com.',
    ],
  },
  {
    date: 'January 7, 2026',
    tag: 'Policy',
    title: 'Updated acceptable-use policy',
    summary:
      'We tightened our acceptable-use language around regulated goods and cross-border tax reporting, with a 30-day notice window for every community.',
    details: [
      'Clearer rules on regulated categories per jurisdiction.',
      'Expanded reporting requirements for cross-border sellers.',
      'Changes took effect Feb 6, 2026, per our published notice window.',
    ],
  },
]

const tagStyles: Record<Tag, string> = {
  Product: 'bg-oak-yellow/30 text-oak-black',
  Cortex: 'bg-oak-green/15 text-oak-green',
  Agents: 'bg-oak-black text-white',
  Platform: 'bg-gold-100 text-gold-800',
  Policy: 'bg-oak-black/5 text-oak-black/70',
}

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.38, 0.005, 0.215, 1] as const },
  },
}

function UpdatesList() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.05 })

  return (
    <section ref={ref} className="rounded-section bg-white px-6 py-20 md:px-16 md:py-28">
      <div className="mx-auto max-w-3xl">
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
          className="space-y-14"
        >
          {updates.map((u) => (
            <motion.article
              key={u.title}
              variants={fadeUp}
              className="border-b border-oak-black/10 pb-14 last:border-b-0"
            >
              <div className="flex flex-wrap items-center gap-3">
                <time className="text-[13px] font-semibold uppercase tracking-[0.08em] text-oak-black/40">
                  {u.date}
                </time>
                <span
                  className={`rounded-full px-3 py-1 text-[12px] font-semibold uppercase tracking-[0.08em] ${tagStyles[u.tag]}`}
                >
                  {u.tag}
                </span>
              </div>
              <h2 className="mt-4 font-serif text-[clamp(26px,3.5vw,40px)] leading-tight tracking-[-1px] text-oak-black">
                {u.title}
              </h2>
              <p className="mt-5 text-[clamp(16px,1.5vw,19px)] leading-[1.6] tracking-[-0.2px] text-oak-black/70">
                {u.summary}
              </p>
              <ul className="mt-6 space-y-3">
                {u.details.map((d) => (
                  <li
                    key={d}
                    className="flex gap-3 text-[clamp(15px,1.4vw,17px)] leading-[1.55] tracking-[-0.2px] text-oak-black/70"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-oak-green" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

function SubscribeSection() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.3 })

  return (
    <section ref={ref} className="bg-oak-cream px-6 py-20 md:px-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
        className="mx-auto max-w-3xl text-center"
      >
        <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
          Stay in the loop
        </span>
        <h2 className="mt-4 font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-oak-black">
          One update a month. Only what actually shipped.
        </h2>
        <p className="mx-auto mt-6 max-w-[460px] text-[clamp(16px,1.5vw,19px)] leading-[1.5] tracking-[-0.2px] text-oak-black/60">
          No newsletter bloat. When the product changes, we write it up here first and send it second.
        </p>
        <a
          href="mailto:updates@oakplatforms.com?subject=Subscribe%20to%20Oak%20updates"
          className="mt-10 inline-flex items-center gap-3 rounded-pill bg-oak-black px-7 py-4 text-[clamp(16px,1.5vw,20px)] font-medium tracking-[-0.4px] text-white transition-opacity hover:opacity-80"
        >
          Subscribe
          <span className="h-8 w-8 rounded-full bg-oak-green" />
        </a>
      </motion.div>
    </section>
  )
}

export default function UpdatesPage() {
  return (
    <PageLayout>
      <PageHero
        eyebrow="Updates"
        title="Everything we have shipped on Oak."
        description="Product changes, platform work, and policy notices — newest first, with enough detail to be useful."
      />
      <UpdatesList />
      <SubscribeSection />
    </PageLayout>
  )
}
